import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { styles } from './superadminStyles';

const CATEGORY_ORDER = ['Auth', 'Bid Outcomes', 'Auction-wide'];

// Renders one template's row in the list — badge reflects the *effective*
// scope shown (a client scope with no override still shows "Inherited",
// since it's the global content being used, not a distinct copy).
const SourceBadge = ({ source }) => {
  const config = {
    override: { label: 'Custom Override', bg: '#1abc9c' },
    global: { label: 'Global Default', bg: '#3498db' },
    default: { label: 'System Default', bg: '#7f8c8d' },
  }[source] || { label: source, bg: '#7f8c8d' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 'bold', background: config.bg }}>
      {config.label}
    </span>
  );
};

const TemplateEditor = ({ template, clientId, onBack, onPublished }) => {
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lastFocused, setLastFocused] = useState('body');
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get(`/superadmin/email-templates/${template.key}/history`, { params: { clientId } });
      setHistory(res.data);
    } catch (err) {
      setError('Failed to load version history.');
    }
  }, [template.key, clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Debounced live preview against this template's sample data.
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await api.post(`/superadmin/email-templates/${template.key}/preview`, { subject, bodyHtml });
        setPreview(res.data);
      } catch (err) {
        // Preview failures shouldn't block editing — just skip refreshing it.
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [template.key, subject, bodyHtml]);

  const insertToken = (tokenName) => {
    const placeholder = `{{${tokenName}}}`;
    if (lastFocused === 'subject') {
      const el = subjectRef.current;
      const pos = el ? el.selectionStart : subject.length;
      setSubject(subject.slice(0, pos) + placeholder + subject.slice(pos));
    } else {
      const el = bodyRef.current;
      const pos = el ? el.selectionStart : bodyHtml.length;
      setBodyHtml(bodyHtml.slice(0, pos) + placeholder + bodyHtml.slice(pos));
    }
  };

  const handlePublish = async () => {
    setError(''); setNotice('');
    if (!subject.trim() || !bodyHtml.trim()) {
      setError('Subject and body cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/superadmin/email-templates/${template.key}`, { clientId, subject: subject.trim(), bodyHtml });
      setNotice('Published new version.');
      await loadHistory();
      onPublished();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to publish template.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setError(''); setNotice('');
    setSendingTest(true);
    try {
      const res = await api.post(`/superadmin/email-templates/${template.key}/send-test`, { subject, bodyHtml });
      setNotice(res.data.msg);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to send test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleRevert = async () => {
    if (!window.confirm('Remove this tenant\'s override and revert to the global default template?')) return;
    setError(''); setNotice('');
    try {
      await api.delete(`/superadmin/email-templates/${template.key}`, { params: { clientId } });
      onPublished();
      onBack();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to revert override.');
    }
  };

  const restoreVersion = (version) => {
    setSubject(version.subject);
    setBodyHtml(version.bodyHtml);
    setNotice(`Loaded v${version.version} into the editor — click Publish to make it current.`);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div>
          <button onClick={onBack} style={{ ...styles.btn, background: 'transparent', border: '1px solid #7f8c8d', padding: '6px 12px', fontSize: '0.8rem', marginBottom: '8px' }}>
            ← Back to Templates
          </button>
          <h3 style={{ margin: 0, color: '#1abc9c' }}>{template.label}</h3>
          <SourceBadge source={template.source} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {template.source === 'override' && (
            <button onClick={handleRevert} style={{ ...styles.btn, background: '#c0392b', padding: '8px 14px', fontSize: '0.85rem' }}>
              Revert to Global Default
            </button>
          )}
          <button onClick={() => setShowHistory((v) => !v)} style={{ ...styles.btn, background: '#3498db', padding: '8px 14px', fontSize: '0.85rem' }}>
            {showHistory ? 'Hide History' : `History (${history.length})`}
          </button>
        </div>
      </div>

      {showHistory && (
        <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '15px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Version</th>
                <th style={{ padding: '8px' }}>Published</th>
                <th style={{ padding: '8px' }}>By</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((v) => (
                <tr key={v._id} style={{ borderBottom: '1px solid #7f8c8d' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold' }}>v{v.version}{v.isCurrent ? ' (current)' : ''}</td>
                  <td style={{ padding: '8px' }}>{new Date(v.createdAt).toLocaleString()}</td>
                  <td style={{ padding: '8px' }}>{v.updatedBy?.name || '—'}</td>
                  <td style={{ padding: '8px' }}>
                    <button onClick={() => restoreVersion(v)} style={{ ...styles.btn, background: '#3498db', padding: '4px 10px', fontSize: '0.75rem' }}>
                      Load into Editor
                    </button>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan="4" style={{ padding: '15px', textAlign: 'center', color: '#bdc3c7' }}>No versions published yet — currently showing the built-in default.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        <div>
          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Subject</label>
            <input
              ref={subjectRef}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setLastFocused('subject')}
              style={styles.input}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Body (HTML)</label>
            <textarea
              ref={bodyRef}
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              onFocus={() => setLastFocused('body')}
              style={{ ...styles.input, height: '320px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </div>

          {error && <div style={{ color: '#e74c3c', marginBottom: '10px', fontSize: '0.9rem' }}>{error}</div>}
          {notice && <div style={{ color: '#1abc9c', marginBottom: '10px', fontSize: '0.9rem' }}>{notice}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button onClick={handleSendTest} disabled={sendingTest} style={{ ...styles.btn, background: '#3498db', opacity: sendingTest ? 0.6 : 1 }}>
              {sendingTest ? 'Sending…' : 'Send Test to Myself'}
            </button>
            <button onClick={handlePublish} disabled={saving} style={{ ...styles.btn, background: '#1abc9c', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Publishing…' : 'Publish New Version'}
            </button>
          </div>
        </div>

        <div>
          <label style={styles.label}>Available Tokens (click to insert)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '20px' }}>
            {template.tokens.map((t) => (
              <button
                key={t.name}
                type="button"
                title={t.description}
                onClick={() => insertToken(t.name)}
                style={{ ...styles.btn, background: '#2c3e50', border: '1px solid #7f8c8d', padding: '4px 8px', fontSize: '0.75rem', fontFamily: 'monospace' }}
              >
                {`{{${t.name}}}`}
              </button>
            ))}
          </div>

          <label style={styles.label}>Live Preview (sample data)</label>
          <div style={{ background: 'white', borderRadius: '4px', overflow: 'hidden', border: '1px solid #7f8c8d' }}>
            <div style={{ background: '#f8f9fa', padding: '8px 10px', fontSize: '0.8rem', color: '#2c3e50', borderBottom: '1px solid #ddd' }}>
              <strong>Subject:</strong> {preview ? preview.subject : '…'}
            </div>
            <iframe
              title="Email preview"
              srcDoc={preview ? preview.html : ''}
              style={{ width: '100%', height: '360px', border: 'none' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailTemplateManagement = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [error, setError] = useState('');

  const loadClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (err) {
      setError('Failed to load client list.');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/superadmin/email-templates', { params: selectedClientId ? { clientId: selectedClientId } : {} });
      setTemplates(res.data);
    } catch (err) {
      setError('Failed to load email templates.');
    }
  }, [selectedClientId]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadTemplates(); setSelectedKey(null); }, [loadTemplates]);

  const selectedTemplate = templates.find((t) => t.key === selectedKey) || null;

  return (
    <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: '4px', color: '#1abc9c' }}>✉️ Email Template Management</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>
            Edit the copy for every automated system email. Changes publish as a new version — past copy is never lost.
          </p>
        </div>
        {!selectedTemplate && (
          <div>
            <label style={{ ...styles.label, marginBottom: '4px' }}>Scope</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              style={{ ...styles.input, width: '220px' }}
            >
              <option value="">Global Default (all tenants)</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.name} (override)</option>)}
            </select>
          </div>
        )}
      </div>

      {error && <div style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '0.9rem' }}>{error}</div>}

      {selectedTemplate ? (
        <TemplateEditor
          template={selectedTemplate}
          clientId={selectedClientId || null}
          onBack={() => setSelectedKey(null)}
          onPublished={loadTemplates}
        />
      ) : (
        CATEGORY_ORDER.map((category) => {
          const categoryTemplates = templates.filter((t) => t.category === category);
          if (categoryTemplates.length === 0) return null;
          return (
            <div key={category} style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#bdc3c7', marginBottom: '8px' }}>{category}</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Template</th>
                    <th style={{ padding: '8px' }}>Status</th>
                    <th style={{ padding: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryTemplates.map((t) => (
                    <tr key={t.key} style={{ borderBottom: '1px solid #7f8c8d' }}>
                      <td style={{ padding: '8px' }}>{t.label}</td>
                      <td style={{ padding: '8px' }}><SourceBadge source={t.source} /></td>
                      <td style={{ padding: '8px' }}>
                        <button onClick={() => setSelectedKey(t.key)} style={{ ...styles.btn, background: '#3498db', padding: '4px 10px', fontSize: '0.8rem' }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
};

export default EmailTemplateManagement;
